import { useState, useRef, useEffect, useCallback } from 'react';

const RichTextEditor = ({ value, onChange, placeholder, error }) => {
  const editorRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [activeFormats, setActiveFormats] = useState(new Set());

  // Initialize editor content
  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  // Handle input changes
  const handleInput = useCallback(() => {
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      onChange(content);
      updateActiveFormats();
    }
  }, [onChange]);

  const handlePaste = useCallback((event) => {
    const items = event.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.indexOf('image') !== -1) {
        event.preventDefault(); // stop default paste
        const file = item.getAsFile();
        const reader = new FileReader();
        reader.onload = () => {
          const imgHTML = `
            <div contenteditable="false" style="display:inline-block; resize:both; overflow:auto; max-width:100%; border: 1px dashed #444; padding: 2px;">
              <img src="${reader.result}" alt="Pasted image" style="display: block; max-width: 100%; height: auto;" />
            </div><br/>
          `;
          document.execCommand('insertHTML', false, imgHTML);
          handleInput();
        };
        reader.readAsDataURL(file);
        break; // only handle the first image
      }
    }
  }, [handleInput]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    editor.addEventListener('paste', handlePaste);
    return () => {
      editor.removeEventListener('paste', handlePaste);
    };
  }, [handlePaste]);

  // Update active format states
  const updateActiveFormats = useCallback(() => {
    const formats = new Set();

    if (document.queryCommandState('bold')) formats.add('bold');
    if (document.queryCommandState('italic')) formats.add('italic');
    if (document.queryCommandState('underline')) formats.add('underline');
    if (document.queryCommandState('strikeThrough')) formats.add('strikethrough');
    if (document.queryCommandState('insertOrderedList')) formats.add('ol');
    if (document.queryCommandState('insertUnorderedList')) formats.add('ul');

    const fontSize = document.queryCommandValue('fontSize');
    if (fontSize === '4') formats.add('h1');
    if (fontSize === '3') formats.add('h2');
    if (fontSize === '2') formats.add('h3');
    
    // Check if selection is inside a link
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      let node = selection.anchorNode;
      while (node && node !== editorRef.current) {
        if (node.nodeName === 'A') {
          formats.add('link');
          break;
        }
        node = node.parentNode;
      }
    }

    setActiveFormats(formats);
  }, []);

  // Execute formatting command
  const execCommand = useCallback((command, value = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    setTimeout(() => {
      handleInput();
      updateActiveFormats();
    }, 0);
  }, [handleInput, updateActiveFormats]);

  const addLink = useCallback(() => {
    const url = prompt('Enter the URL:');
    if (url) {
      execCommand('createLink', url);
    }
  }, [execCommand]);

  // Handle key shortcuts
  const handleKeyDown = useCallback((e) => {
    if (e.ctrlKey || e.metaKey) {
      switch (e.key) {
        case 'b':
          e.preventDefault();
          execCommand('bold');
          break;
        case 'i':
          e.preventDefault();
          execCommand('italic');
          break;
        case 'u':
          e.preventDefault();
          execCommand('underline');
          break;
        case 'z':
          e.preventDefault();
          execCommand(e.shiftKey ? 'redo' : 'undo');
          break;
        case 'k':
          e.preventDefault();
          addLink();
          break;
      }
    }
  }, [execCommand, addLink]);

  // Toolbar button component
  const ToolbarButton = ({ command, value, icon, title, isActive, className = '', onClick }) => (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        if (onClick) onClick(); else execCommand(command, value);
      }}
      className={`p-2 rounded hover:bg-gray-600 transition-colors ${isActive ? 'bg-blue-600 text-white' : 'text-gray-300'
        } ${className}`}
      title={title}
    >
      {icon}
    </button>
  );

  // Remove formatting
  const removeFormat = useCallback(() => {
    editorRef.current?.focus();
    document.execCommand('removeFormat', false, null);
    document.execCommand('unlink', false, null);
    setTimeout(() => {
      handleInput();
      updateActiveFormats();
    }, 0);
  }, [handleInput, updateActiveFormats]);

  return (
    <div className={`border rounded-lg ${error ? 'border-red-500' : 'border-gray-600'} ${isFocused ? 'ring-2 ring-blue-500' : ''}`}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-gray-600 bg-gray-800/50">
        {/* Text formatting */}
        <div className="flex items-center gap-1">
          <ToolbarButton
            command="bold"
            icon={<span className="font-bold">B</span>}
            title="Bold (Ctrl+B)"
            isActive={activeFormats.has('bold')}
          />
          <ToolbarButton
            command="italic"
            icon={<span className="italic">I</span>}
            title="Italic (Ctrl+I)"
            isActive={activeFormats.has('italic')}
          />
          <ToolbarButton
            command="underline"
            icon={<span className="underline">U</span>}
            title="Underline (Ctrl+U)"
            isActive={activeFormats.has('underline')}
          />
          <ToolbarButton
            command="strikeThrough"
            icon={<span className="line-through">S</span>}
            title="Strikethrough"
            isActive={activeFormats.has('strikethrough')}
          />
          <ToolbarButton
            icon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            }
            title="Insert Link (Ctrl+K)"
            isActive={activeFormats.has('link')}
            onClick={addLink}
          />
          <ToolbarButton
            icon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276a1 1 0 011.447.894v6.764a1 1 0 01-1.447.894L15 14M4 6h16M4 10h16M4 14h8" />
              </svg>
            }
            title="Paste Image (Ctrl+V)"
            onClick={() => {
              editorRef.current?.focus();
              alert('Now press Ctrl+V to paste your image from the clipboard.');
            }}
          />
        </div>

        <div className="w-px h-6 bg-gray-600 mx-1" />

        {/* Headings */}
        <div className="flex items-center gap-1">
          <ToolbarButton
            command="fontSize"
            value="4"
            icon="H1"
            title="Heading 1"
            isActive={activeFormats.has('h1')}
            className="text-sm font-bold"
          />
          <ToolbarButton
            command="fontSize"
            value="3"
            icon="H2"
            title="Heading 2"
            isActive={activeFormats.has('h2')}
            className="text-sm font-bold"
          />
          <ToolbarButton
            command="fontSize"
            value="2"
            icon="H3"
            title="Heading 3"
            isActive={activeFormats.has('h3')}
            className="text-sm font-bold"
          />
        </div>

        <div className="w-px h-6 bg-gray-600 mx-1" />

        {/* Lists */}
        <div className="flex items-center gap-1">
          <ToolbarButton
            command="insertUnorderedList"
            icon={
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M3 4a1 1 0 000 2h1a1 1 0 000-2H3zM3 8a1 1 0 000 2h1a1 1 0 000-2H3zM3 12a1 1 0 100 2h1a1 1 0 100-2H3zM7 4a1 1 0 000 2h10a1 1 0 100-2H7zM7 8a1 1 0 000 2h10a1 1 0 100-2H7zM7 12a1 1 0 100 2h10a1 1 0 100-2H7z" />
              </svg>
            }
            title="Bullet List"
            isActive={activeFormats.has('ul')}
          />
          <ToolbarButton
            command="insertOrderedList"
            icon={
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M3 4a1 1 0 000 2v1a1 1 0 102 0V6a1 1 0 000-2H3zM3 8a1 1 0 000 2v1a1 1 0 102 0V10a1 1 0 000-2H3zM3 12a1 1 0 100 2v1a1 1 0 102 0v-1a1 1 0 10-2 0zM7 4a1 1 0 000 2h10a1 1 0 100-2H7zM7 8a1 1 0 000 2h10a1 1 0 100-2H7zM7 12a1 1 0 100 2h10a1 1 0 100-2H7z" />
              </svg>
            }
            title="Numbered List"
            isActive={activeFormats.has('ol')}
          />
        </div>

        <div className="w-px h-6 bg-gray-600 mx-1" />

        {/* Alignment */}
        <div className="flex items-center gap-1">
          <ToolbarButton
            command="justifyLeft"
            icon={
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 4a1 1 0 011-1h14a1 1 0 110 2H3a1 1 0 01-1-1zM2 8a1 1 0 011-1h8a1 1 0 110 2H3a1 1 0 01-1-1zM2 12a1 1 0 011-1h14a1 1 0 110 2H3a1 1 0 01-1-1zM2 16a1 1 0 011-1h8a1 1 0 110 2H3a1 1 0 01-1-1z" />
              </svg>
            }
            title="Align Left"
          />
          <ToolbarButton
            command="justifyCenter"
            icon={
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 4a1 1 0 011-1h14a1 1 0 110 2H3a1 1 0 01-1-1zM5 8a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zM2 12a1 1 0 011-1h14a1 1 0 110 2H3a1 1 0 01-1-1zM5 16a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z" />
              </svg>
            }
            title="Align Center"
          />
          <ToolbarButton
            command="justifyRight"
            icon={
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 4a1 1 0 011-1h14a1 1 0 110 2H3a1 1 0 01-1-1zM8 8a1 1 0 011-1h8a1 1 0 110 2H9a1 1 0 01-1-1zM2 12a1 1 0 011-1h14a1 1 0 110 2H3a1 1 0 01-1-1zM8 16a1 1 0 011-1h8a1 1 0 110 2H9a1 1 0 01-1-1z" />
              </svg>
            }
            title="Align Right"
          />
        </div>

        <div className="w-px h-6 bg-gray-600 mx-1" />

        {/* Additional tools */}
        <div className="flex items-center gap-1">
          <ToolbarButton
            command="insertHorizontalRule"
            icon={<span className="text-xs">HR</span>}
            title="Insert Horizontal Rule"
          />
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              removeFormat();
            }}
            className="p-2 rounded hover:bg-gray-600 transition-colors text-gray-300"
            title="Remove Formatting"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
              <path fillRule="evenodd" d="M4 5a2 2 0 012-2v1a3 3 0 003 3h2a3 3 0 003-3V3a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V5zM7 8a1 1 0 012 0v2a1 1 0 11-2 0V8zm4 0a1 1 0 112 0v2a1 1 0 11-2 0V8z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        className="p-4 min-h-[300px] bg-gray-800 text-white focus:outline-none"
        onInput={handleInput}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onKeyDown={handleKeyDown}
        onMouseUp={updateActiveFormats}
        onKeyUp={updateActiveFormats}
        data-placeholder={placeholder}
        style={{
          wordBreak: 'break-word',
          overflowWrap: 'break-word'
        }}
        suppressContentEditableWarning={true}
      />

      {/* Character count */}
      <div className="px-4 py-2 text-xs text-gray-400 bg-gray-800/30 border-t border-gray-600">
        {editorRef.current?.textContent?.length || 0} characters
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #6b7280;
          pointer-events: none;
        }
        
        [contenteditable] h1 { font-size: 1.875rem; font-weight: bold; margin: 0.5em 0; }
        [contenteditable] h2 { font-size: 1.5rem; font-weight: bold; margin: 0.5em 0; }
        [contenteditable] h3 { font-size: 1.25rem; font-weight: bold; margin: 0.5em 0; }
        [contenteditable] p { margin: 0.5em 0; }
        [contenteditable] ul, [contenteditable] ol { margin: 0.5em 0; padding-left: 2em; }
        [contenteditable] li { margin: 0.25em 0; }
        [contenteditable] a { color: #60a5fa; text-decoration: underline; cursor: pointer; }
        [contenteditable] hr { border: none; border-top: 1px solid #4b5563; margin: 1em 0; }
        [contenteditable] strong { font-weight: bold; }
        [contenteditable] em { font-style: italic; }
        [contenteditable] u { text-decoration: underline; }
        [contenteditable] s { text-decoration: line-through; }
        [contenteditable] img { max-width: 100%; height: auto; display: block; margin: 0.5em 0; object-fit: contain; resize: both; overflow: auto;}
        [contenteditable] img:focus { outline: 2px solid #60a5fa;}
        [contenteditable] div[contenteditable="false"] {
          resize: both;
          overflow: auto;
          display: inline-block;
          max-width: 100%;
          margin: 0.5em 0;
          border: 1px dashed #4b5563;
          padding: 4px;
          background-color: #111;
        }
        [contenteditable] div[contenteditable="false"]:focus {
          outline: 2px solid #60a5fa;
        }
      `}} />
    </div>
  );
};

export default RichTextEditor;
